import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import SummarizationLoaderComponent from 'components/ai/SummarizationLoaderComponent';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import { useStageTimer } from 'hooks/useStageTimer';
import AIJob from 'models/AIJob';

type ComparisonLoadingStateProps = {
  comparisonJob: AIJob | null;
  onLoadingFinished: () => void;
};

export default function ComparisonLoadingState({ comparisonJob, onLoadingFinished }: ComparisonLoadingStateProps) {
  const comparisonJobCreatedAt = comparisonJob?.createdAt;

  const onSecondStage = useStageTimer(comparisonJobCreatedAt, 30);
  const onThirdStage = useStageTimer(comparisonJobCreatedAt, 60);

  const stage = useMemo(() => {
    if (comparisonJob?.isCompleted) {
      return 4;
    }
    if (onThirdStage) {
      return 3;
    }
    if (onSecondStage) {
      return 2;
    }
    return 1;
  }, [comparisonJob, onSecondStage, onThirdStage]);

  const [currentStage, setCurrentStage] = useState(stage);

  useEffect(() => {
    setCurrentStage(stage);
  }, [stage]);

  const stages = useMemo(
    () => [{ title: 'Comparing summaries' }, { title: 'Formatting comparison' }, { title: 'Finishing up' }],
    []
  );

  return (
    <LoaderContainer>
      <SummarizationLoaderComponent
        type='comparison'
        stages={stages}
        stage={currentStage}
        onLoadingFinished={onLoadingFinished}
      />
      <TimingExplainer jobType='comparison' />
    </LoaderContainer>
  );
}

const LoaderContainer = styled.div`
  margin: 56px 0 112px 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;
